// Animal search for /search?q=<term>&view=pets&field=<field>, with infinite scroll like the pet pages.
// field=type with a known pet type ("dog", "small animal") lists that metaobject type through
// /search?q=<type>&view=pets-json (Liquid, Active pets only, A-Z like /pages/dog).
// Other fields use Surge when enabled in theme settings, with the prefs app (/apps/prefs/animals) as the fallback,
// also used when Surge errors or finds nothing (its breed index is incomplete). Results keep the order Surge returns.
const PET_SEARCH_FIELDS = {
  breed: { surge: 'breed', prefs: 'breed' },
  feature: { surge: 'feature', prefs: 'feature' },
  location: { surge: 'locations', prefs: 'location' },
  pet_type: { surge: 'pet_type', prefs: 'pet_type' },
  pet_subtype: { surge: 'pet_subtype', prefs: 'subtype' }, // prefs app: /animals?subtype=designer
};
const PET_SEARCH_ALIASES = { type: 'pet_type', subtype: 'pet_subtype', locations: 'location' };
const PET_SEARCH_PER_PAGE = 16; // cards added per scroll step
const PET_SEARCH_BATCH = 250; // results fetched per request

class NewSearchByQuery extends HTMLElement {
  connectedCallback() {
    const params = new URLSearchParams(window.location.search);
    const fieldParam = (params.get('field') || '').trim().toLowerCase();
    const fieldKey = PET_SEARCH_ALIASES[fieldParam] || fieldParam;
    this.query = (this.dataset.q || '').trim();
    // Theme settings > Search API > Pet metaobject types, e.g. ncr_live_animal_pet_dog,ncr_live_animal_pet_cat
    this.petTypes = (this.dataset.petTypes || '').split(',').filter(Boolean);
    this.field = PET_SEARCH_FIELDS[fieldKey];
    this.metaType = fieldKey === 'pet_type' && this.resolveType(this.query);
    this.source = this.metaType ? 'liquid' : this.dataset.surge === 'true' ? 'surge' : 'prefs';
    this.page = 1;
    this.cursor = null;
    this.nextUrl = null;
    this.remoteDone = false;
    this.buffer = [];
    this.resultsWrapper = this.querySelector('.new-search-page-results-by-query');
    this.loader = this.querySelector('.new-meta-pet-list-pagination');
    // Load the next cards as the loader nears the viewport, like Ajaxinate on the pet pages
    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.load();
    }, { rootMargin: '0px 0px 300px 0px' });
    this.load();
  }

  // "dogs" / "Small Animal" -> ncr_live_animal_pet_dog / ncr_live_animal_pet_small_animal (the part after "_pet_")
  resolveType(text) {
    const slug = text.trim().toLowerCase().replace(/[\s-]+/g, '_').replace(/s$/, '');
    return slug ? this.petTypes.find((type) => type === slug || type.split('_pet_').pop() === slug) : undefined;
  }

  async load() {
    if (this.isLoading || (this.remoteDone && !this.buffer.length && this.started)) return;
    this.isLoading = true;
    const firstLoad = !this.started;
    this.started = true;
    if (firstLoad) this.setState('searching');
    this.loader?.classList.add('is-loading');

    if (this.buffer.length < PET_SEARCH_PER_PAGE && !this.remoteDone && (this.field || this.metaType) && this.query) {
      this.buffer = this.buffer.concat(await this.fetchBatch(firstLoad));
    }

    const cards = this.buffer.splice(0, PET_SEARCH_PER_PAGE);
    this.resultsWrapper.insertAdjacentHTML('beforeend', cards.map((pet) => this.renderCard(pet)).join(''));
    const hasCards = !!this.resultsWrapper.querySelector('.new-search-page-result-item');
    this.setState(hasCards ? 'searched' : 'not-found');

    const hasMore = this.buffer.length > 0 || !this.remoteDone;
    this.loader?.classList.remove('is-loading');
    if (this.loader) this.loader.hidden = !hasMore;
    this.isLoading = false;
    if (hasMore) {
      this.observer.unobserve(this.loader);
      this.observer.observe(this.loader); // re-check: fires again at once if the loader is still in view
    } else {
      this.observer.disconnect();
    }
  }

  // One batch from the current source; on the first load Liquid/Surge fall back when they error or find nothing
  async fetchBatch(firstLoad) {
    const fallbacks = { liquid: this.field ? (this.dataset.surge === 'true' ? 'surge' : 'prefs') : null, surge: 'prefs', prefs: null };
    while (this.source) {
      try {
        const items = await (this.source === 'liquid' ? this.fetchLiquid() : this.source === 'surge' ? this.fetchSurge() : this.fetchPrefs());
        if (items.length || !firstLoad || !fallbacks[this.source]) return items;
      } catch (error) {
        console.error(`Pet search (${this.source}) failed`, error);
        if (!firstLoad) break;
      }
      if (!firstLoad) break;
      this.source = fallbacks[this.source];
      this.page = 1;
      this.cursor = null;
      this.nextUrl = null;
      this.remoteDone = false;
    }
    this.remoteDone = true;
    return [];
  }

  async fetchLiquid() {
    const url = this.nextUrl || `${window.Shopify?.routes?.root || '/'}search?q=${encodeURIComponent(this.metaType)}&view=pets-json`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`pets-json ${response.status}`);
    const data = await response.json();
    // paginate names its page param per tag (e.g. page_f28b8882); next_page already carries it, plus view/q
    this.nextUrl = data.next_page || null;
    this.remoteDone = !this.nextUrl;
    return (data.pets || []).map((pet) => ({ name: pet.name, breed: pet.breed, dob: pet.dob, url: pet.url, image: pet.image }));
  }

  async fetchSurge() {
    const response = await fetch('/apps/surge/multi_search', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        searches: [{
          collection: 'meta_objects',
          q: this.query,
          query_by: `values.${this.field.surge}`,
          filter_by: `type:[${this.petTypes.join(',')}]`,
          per_page: PET_SEARCH_BATCH,
          page: this.page,
        }],
        source: 'search_page',
        search_context: 'instant_search',
      }),
    });
    if (!response.ok) throw new Error(`Surge ${response.status}`);
    const [result] = (await response.json()).results || [];
    if (!result || result.error) throw new Error(result?.error || 'Surge returned no results');

    const items = (result.hits || []).map(({ document: doc }) => ({
      name: surgeValue(doc.values?.name),
      breed: surgeValue(doc.values?.breed),
      dob: surgeValue(doc.values?.dob),
      url: `/pages/${doc.type.replaceAll('_', '-')}/${doc.handle}`,
      image: doc.imageUrls?.[0],
    }));
    this.remoteDone = this.page * PET_SEARCH_BATCH >= (result.found || 0);
    this.page += 1;
    return items;
  }

  async fetchPrefs() {
    const params = new URLSearchParams({ [this.field.prefs]: this.query });
    if (this.cursor) params.set('cursor', this.cursor);
    const response = await fetch(`/apps/prefs/animals?${params}`);
    if (!response.ok) throw new Error(`Prefs ${response.status}`);
    const data = await response.json();
    this.cursor = data.pageInfo?.hasNextPage ? data.pageInfo.nextCursor : null;
    this.remoteDone = !this.cursor;
    return (data.items || []).map((pet) => ({ name: surgeValue(pet.name), breed: surgeValue(pet.breed), dob: surgeValue(pet.dob), url: pet.url, image: pet.images?.[0] }));
  }

  setState(state) {
    ['searching', 'searched', 'not-found'].forEach((name) => this.resultsWrapper.classList.toggle(name, name === state));
  }

  renderCard(pet) {
    // NCR copies the name into breed when an animal has no real breed (e.g. tarantula kits)
    const breed = pet.breed && pet.breed.toLowerCase() !== (pet.name || '').toLowerCase() ? pet.breed : '';
    const image = pet.image ? `${pet.image}${pet.image.includes('?') ? '&' : '?'}width=500` : window.petCardPlaceholder;
    return `
      <div class="new-search-page-result-item">
        <a href="${escapeHTML(pet.url)}" class="new-pet-card zoom-bg" style="--pet-image:
          linear-gradient(180deg, rgba(0, 0, 0, 0) 74.22%, rgba(33, 33, 33, 0.6) 100%),
          linear-gradient(0.24deg, rgba(0, 0, 0, 0.1) 0.31%, rgba(0, 0, 0, 0) 99.9%),
          url('${encodeURI(image)}');
        ">
          <h2 class="new-pet-card-name">${escapeHTML(pet.name)}</h2>
          <div class="new-pet-card-desc">
            ${pet.dob ? `<p class="new-pet-card-dob"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.5 4.50391C13.5 5.33234 12.8284 6.00391 12 6.00391C11.1716 6.00391 10.5 5.33234 10.5 4.50391C10.5 3.67548 12 2.00391 12 2.00391C12 2.00391 13.5 3.67548 13.5 4.50391Z" stroke="currentColor" stroke-width="1.212" stroke-linejoin="round"></path><path d="M12.002 6V9" stroke="currentColor" stroke-width="1.212" stroke-linecap="round" stroke-linejoin="round"></path><path d="M17.6667 14C19.2315 14 20.5 12.8807 20.5 11.5C20.5 10.1193 19.2315 9 17.6667 9H6.33333C4.76853 9 3.5 10.1193 3.5 11.5C3.5 12.8807 4.76853 14 6.33333 14C7.70408 14 8.90415 13.1411 9.16667 12C9.42919 13.1411 10.6293 14 12 14C13.3707 14 14.5708 13.1411 14.8333 12C15.0959 13.1411 16.2959 14 17.6667 14Z" stroke="currentColor" stroke-width="1.212" stroke-linejoin="round"></path><path d="M5 14.0039L5.52089 16.5835C6.04532 19.1807 6.30754 20.4793 7.19608 21.2416C8.08462 22.0039 9.33608 22.0039 11.839 22.0039H12.161C14.6639 22.0039 15.9154 22.0039 16.8039 21.2416C17.6925 20.4793 17.9547 19.1807 18.4791 16.5835L19 14.0039" stroke="currentColor" stroke-width="1.212" stroke-linecap="round" stroke-linejoin="round"></path></svg>${escapeHTML(pet.dob)}</p>` : ''}
            ${breed ? `<p class="new-pet-card-breed"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16.1041 14.0898V14.6035M11.2246 16.4012H12.7655L11.9951 17.1716L11.2246 16.4012Z" stroke="currentColor" stroke-width="1.212" stroke-linecap="round" stroke-linejoin="round"></path><path d="M4.20879 11.2617C3.92182 12.372 3.77685 13.5142 3.77735 14.6609C3.77735 18.9466 7.45698 21.2806 11.9954 21.2806C16.5338 21.2806 20.2135 18.9466 20.2135 14.6609C20.2083 13.5094 20.0378 12.3647 19.707 11.2617M7.88637 14.0898V14.6034" stroke="currentColor" stroke-width="1.212" stroke-linecap="round" stroke-linejoin="round"></path><path d="M8.40101 8.43552C8.00655 9.51414 7.28849 10.5188 5.99312 11.0037C4.00949 11.7453 2.31965 10.6986 2.23747 9.97641C2.12139 8.95531 3.44655 3.26842 6.3465 2.78561C8.32191 2.45586 10.097 3.65364 10.097 5.08153C11.3963 4.7517 12.7591 4.76657 14.0509 5.12467C14.0509 3.69679 15.9452 2.45586 17.9206 2.78561C20.8205 3.26842 22.1457 8.95531 22.0296 9.97641C21.9474 10.6986 20.2576 11.7453 18.274 11.0037C16.9786 10.5188 16.3684 9.51414 15.9739 8.43552" stroke="currentColor" stroke-width="1.212" stroke-linecap="round" stroke-linejoin="round"></path></svg>${escapeHTML(breed)}</p>` : ''}
          </div>
        </a>
      </div>`;
  }
}
customElements.define('new-search-by-query', NewSearchByQuery);
